import React, { useCallback, useState, useEffect } from "react";
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Show,
  Text,
  useColorMode,
  Box,
  Center,
} from "@chakra-ui/react";
import Image from "next/image";
import { FiChevronDown } from "react-icons/fi";
import usePageData from "@hooks/usePageData";
import { useDappSDKProvider } from "dapp-sdk-v2";

const subRoute = process.env.NEXT_PUBLIC_SUB_ROUTE || "";
const token = process.env.NEXT_PUBLIC_TOKEN || "";

interface NetworkProps {
  chain_name: string;
  is_native: boolean;
  chain_id: string;
  icon_key: string;
}

interface NetworkSwitchProps {
  onSwitch?: (data: NetworkProps) => void;
}

const NetworkSwitch = ({ onSwitch }: NetworkSwitchProps) => {
  const { staking_network } = usePageData("$.page.data")[0] || {};

  const { colorMode } = useColorMode();
  const [activeChain, setActiveChain] = useState<any>(
    staking_network.chains ? staking_network.chains[0] : null
  );

  const { config, isEmbed, isSafeApp, setUserData } = useDappSDKProvider();

  useEffect(() => {
    let isNative: any = window.localStorage.getItem(`${token}_is_native`);
    isNative = isNative !== "false";

    setTimeout(() => {
      setUserData({
        isNativeChain: isNative,
      });
    }, 1000);

    if (staking_network.chains) {
      staking_network.chains.forEach((item: NetworkProps) => {
        if (item.is_native === isNative) {
          setActiveChain(item);
        }
      });
    }
  }, []);

  useEffect(() => {
    if ((isEmbed || isSafeApp) && staking_network.chains && config) {
      const network = staking_network.chains.find(
        (item: NetworkProps) => +item.chain_id === +config.chainId
      );

      if (!network) {
        return;
      }

      setActiveChain(network);
      setUserData({
        isNativeChain: network.is_native,
      });

      window.localStorage.setItem(
        `${token}_is_native`,
        network.is_native ? "true" : "false"
      );
    }
  }, [isEmbed, isSafeApp, config, staking_network.chains]);

  const isActiveChain = (data: NetworkProps) => {
    return data.chain_name === activeChain.chain_name;
  };

  const handleSwitch = useCallback(
    (data: NetworkProps) => {
      setActiveChain(data);
      setUserData({
        isNativeChain: data.is_native,
      });
      window.localStorage.setItem(
        `${token}_is_native`,
        data.is_native ? "true" : "false"
      );
      onSwitch && onSwitch(data);
    },
    [activeChain]
  );

  if (staking_network && staking_network.enabled && !isEmbed && !isSafeApp) {
    return (
      <>
        <Show above="md">
          <Flex
            h={{ lg: "56px", md: "32px", base: "32px" }}
            bg="backgroundSecondary"
            p={{ base: "8px 16px", lg: "8px 16px" }}
            gap="0.5rem"
            color="textPrimary"
            alignItems="center"
            fontSize="14px"
            borderRadius="8px"
          >
            <Text>{staking_network.title}</Text>
            {staking_network?.chains.map((item: NetworkProps) => {
              return (
                <Flex
                  key={item.chain_name}
                  as="button"
                  p={{ lg: "6.4px 16px", md: "2px 10px", base: "3px 10px" }}
                  cursor="pointer"
                  onClick={() => handleSwitch(item)}
                  gap="0.5rem"
                  border="1px"
                  borderColor={
                    isActiveChain(item) ? "primary" : "backgroundSecondary"
                  }
                  borderRadius="8px"
                >
                  <Image
                    height={20}
                    width={20}
                    src={`${subRoute}/assets/images/sidebar/${item.icon_key.toLowerCase()}_${colorMode}.svg`}
                    alt={item.icon_key}
                  />
                  <Text
                    display={{
                      base: "none",
                      md: "block",
                      lg: "block",
                    }}
                  >
                    {item.chain_name}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </Show>

        <Show below="md">
          <Menu>
            <MenuButton
              as={Button}
              h="32px"
              bg="backgroundSecondary"
              color="textPrimary"
              _focus={{
                bg: "backgroundSecondary",
              }}
              _hover={{
                bg: "backgroundSecondary",
              }}
              rightIcon={<FiChevronDown />}
            >
              <Center>
                <Image
                  height={20}
                  width={20}
                  src={`${subRoute}/assets/images/sidebar/${activeChain?.icon_key?.toLowerCase()}_${colorMode}.svg`}
                  alt={activeChain.icon_key}
                />
              </Center>
            </MenuButton>
            <MenuList bg="backgroundSecondary">
              {staking_network?.chains.map((item: NetworkProps) => {
                return (
                  <MenuItem
                    key={item.chain_name}
                    _focus={{
                      bg: "backgroundSecondary",
                    }}
                    _hover={{
                      bg: "backgroundSecondary",
                    }}
                  >
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      w="100%"
                    >
                      <Flex
                        alignItems="center"
                        as="button"
                        p="3px 10px"
                        cursor="pointer"
                        onClick={() => handleSwitch(item)}
                        gap="0.5rem"
                      >
                        <Image
                          height={20}
                          width={20}
                          src={`${subRoute}/assets/images/sidebar/${item.icon_key.toLowerCase()}_${colorMode}.svg`}
                          alt={item.icon_key}
                        />
                        <Text>{item.chain_name}</Text>
                      </Flex>
                      {isActiveChain(item) && (
                        <Box bg="primary" h={2} w={2} borderRadius={360} />
                      )}
                    </Flex>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        </Show>
      </>
    );
  }
  return null;
};

export default NetworkSwitch;
